// components/CallWidget/CallWidget.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  makeStyles,
} from "@material-ui/core";
import {
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
  Note as NoteIcon,
} from "@mui/icons-material";
import { useTwilioCall } from "../../../../../../hooks/useTwilioCall";
import { contactsTypesActions } from "../../../../../../store/contactTypes/contactTypesActions";
import { useDispatch } from "react-redux";

const useStyles = makeStyles(theme => ({
  dialog: {
    borderRadius: "12px",
    minWidth: "420px",
    maxWidth: "500px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    padding: theme.spacing(3),
    textAlign: "center",
  },
  avatar: {
    width: 80,
    height: 80,
    margin: "0 auto 16px",
    backgroundColor: theme.palette.primary.light,
    fontSize: "32px",
  },
  prospectName: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "4px",
  },
  phoneNumber: {
    fontSize: "16px",
    color: theme.palette.text.secondary,
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  statusContainer: {
    margin: "24px 0",
    minHeight: "40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  statusText: {
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "8px",
    "&.ready": { color: theme.palette.success.main },
    "&.calling": { color: theme.palette.warning.main },
    "&.connected": { color: theme.palette.success.main },
    "&.error": { color: theme.palette.error.main },
  },
  callDuration: {
    fontSize: "32px",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    margin: "16px 0",
    fontFamily: "monospace",
    letterSpacing: "2px",
  },
  callButton: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    margin: "24px auto",
    "&.makeCall": {
      backgroundColor: theme.palette.success.main,
      "&:hover": {
        backgroundColor: theme.palette.success.dark,
      },
    },
    "&.endCall": {
      backgroundColor: theme.palette.error.main,
      "&:hover": {
        backgroundColor: theme.palette.error.dark,
      },
    },
    "&:disabled": {
      backgroundColor: theme.palette.action.disabled,
      cursor: "not-allowed",
    },
  },
  errorBox: {
    backgroundColor: theme.palette.error.light,
    color: "white",
    padding: theme.spacing(2),
    borderRadius: "8px",
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(4, 0),
  },
  callInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  remarksSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    animation: "$fadeIn 0.5s ease-in",
  },
  remarksHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    justifyContent: "center",
    color: theme.palette.primary.main,
  },
  connectCheckbox: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
  },
  remarksTextarea: {
    width: "100%",
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
    "& textarea": {
      minHeight: "100px",
      resize: "vertical",
    },
  },
  saveButton: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 3),
  },
  savingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  successMessage: {
    color: "white",
    backgroundColor: theme.palette.success.light,
    padding: theme.spacing(1, 2),
    borderRadius: "4px",
    marginTop: theme.spacing(1),
  },
  successMessageTxt: {
    fontSize: "16px",
  },
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(10px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const CallWidget = ({
  open,
  onClose,
  prospect,
  onCallComplete,
  loadLeadsGeneration,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    isInitialized,
    isCallActive,
    callStatus,
    callDuration,
    error,
    initialize,
    makeCall,
    hangUp,
    getFormattedDuration,
    setError,
  } = useTwilioCall();

  const [isLoading, setIsLoading] = useState(false);
  const [twilioToken, setTwilioToken] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [showRemarks, setShowRemarks] = useState(false);
  const [isSavingRemarks, setIsSavingRemarks] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isConnected, setIsConnected] = useState(false); // New state for checkbox

  // Fetch Twilio token when widget opens
  useEffect(() => {
    const fetchToken = async () => {
      if (open && prospect) {
        setIsLoading(true);
        console.log("Fetching token for prospect:", prospect.id);

        try {
          const response = await dispatch(
            contactsTypesActions.onGetVoiceToke()
          );

          if (response && response.token) {
            setTwilioToken(response.token);
          } else {
            throw new Error("Failed to get call token");
          }
        } catch (err) {
          console.error("Error fetching token:", err);
          setError("Failed to initialize call service: " + err.message);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (open) {
      // Reset states when widget opens
      setRemarks("");
      setShowRemarks(false);
      setIsSavingRemarks(false);
      setSaveSuccess(false);
      setIsConnected(false); // Reset checkbox state

      fetchToken();
    }
  }, [open, prospect, dispatch, setError]);

  // Initialize Twilio when token is available
  useEffect(() => {
    const initTwilio = async () => {
      if (twilioToken && !isInitialized) {
        try {
          await initialize(twilioToken);
        } catch (err) {
          console.error("Failed to initialize Twilio:", err);
          setError("Failed to setup call service");
        }
      }
    };

    initTwilio();
  }, [twilioToken, isInitialized, initialize, setError]);

  // Monitor call status to show remarks when call ends
  useEffect(() => {
    // Show remarks section when call ends (disconnected, error, or manually ended)
    if (
      !isCallActive &&
      (callStatus === "disconnected" || callStatus === "error")
    ) {
      // Wait a moment before showing remarks for better UX
      const timer = setTimeout(() => {
        setShowRemarks(true);
      }, 500);

      return () => clearTimeout(timer);
    }

    // Hide remarks if call becomes active again
    if (isCallActive) {
      setShowRemarks(false);
      setSaveSuccess(false);
    }
  }, [isCallActive, callStatus]);

  // Handle making a call
  const handleMakeCall = async () => {
    if (!prospect?.contactNo) {
      setError("No contact number available");
      return;
    }

    try {
      // Reset remarks state when starting new call
      setRemarks("");
      setShowRemarks(false);
      setSaveSuccess(false);
      setIsConnected(false); // Reset checkbox state

      await makeCall(prospect.contactNo, prospect);
    } catch (err) {
      console.error("Call failed:", err);
      setError("Call failed: " + err.message);
    }
  };

  // Handle ending a call
  const handleEndCall = async () => {
    try {
      await hangUp();

      if (onCallComplete) {
        onCallComplete();
      }
    } catch (err) {
      console.error("Error ending call:", err);
    }
  };

  // Check if save button should be enabled
  const isSaveButtonEnabled = () => {
    return isConnected || remarks.trim().length > 0;
  };

  // Handle saving remarks
  const handleSaveRemarks = async () => {
    if (!isConnected && !remarks.trim()) {
      setError(
        "Please either check 'Is Contacted' or enter remarks before saving"
      );
      return;
    }

    setIsSavingRemarks(true);
    setError(""); // Clear any previous errors

    try {
      // Prepare payload according to your API structure
      const todayDate = new Date().toISOString();

      const payload = {
        prospectId: prospect.id,
        comments: remarks.trim(),
        type: 1, // Assuming 1 is for call remarks/interaction type
        interactionDate: todayDate,
        isContacted: isConnected, // Add checkbox value to payload
      };

      console.log("Saving call remarks with payload:", payload);

      // Make API call using your Redux action
      const response = await dispatch(
        contactsTypesActions.onAddProspectInteraction(payload)
      );

      console.log("Save remarks response:", response);

      // Check if the response indicates success
      // Adjust this condition based on your actual API response structure
      if (response?.data?.success) {
        loadLeadsGeneration();
        // Success - update state
        setSaveSuccess(true);
        setIsSavingRemarks(false);

        // Clear remarks after successful save
        setRemarks("");

        // Hide remarks section after 2 seconds
        setTimeout(() => {
          setShowRemarks(false);
        }, 2000);

        // Trigger callback if provided
        if (onCallComplete) {
          onCallComplete({
            ...prospect,
            lastInteraction: todayDate,
            remarks: remarks.trim(),
            callDuration: callDuration,
            isConnected: isConnected, // Include checkbox value in callback
          });
        }
      } else {
        // Handle API error - adjust based on your API response structure
        const errorMessage =
          response?.message ||
          response?.error ||
          "Failed to save. Please try again.";
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Error saving remarks:", err);
      setError("Failed to save: " + err.message);
      setIsSavingRemarks(false);
    }
  };

  // Handle checkbox change
  const handleConnectCheckboxChange = event => {
    setIsConnected(event.target.checked);
  };

  // Handle widget close
  const handleClose = () => {
    if (isCallActive) {
      if (
        window.confirm("You are on a call. Are you sure you want to end it?")
      ) {
        handleEndCall();
      } else {
        return; // Don't close if user cancels
      }
    }

    // Check if there are unsaved remarks or unchecked connection status
    if ((remarks.trim() || isConnected) && !saveSuccess) {
      if (
        window.confirm("You have unsaved information. Close without saving?")
      ) {
        onClose();
      } else {
        return;
      }
    } else {
      onClose();
    }
  };

  // Get status message
  const getStatusMessage = () => {
    if (isLoading) return "Setting up call...";

    if (saveSuccess) return " ";

    switch (callStatus) {
      case "initializing":
        return "Initializing call service...";
      case "ready":
        return "Ready to call";
      case "calling":
        return "Calling...";
      case "connected":
        return "Connected";
      case "disconnected":
        return "Call ended";
      case "error":
        return "Call failed";
      default:
        return "Ready";
    }
  };

  if (!prospect) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      PaperProps={{ className: classes.dialog }}>
      <DialogTitle disableTypography className={classes.header}>
        <Typography variant='h6'>
          {isCallActive ? "Active Call" : "Call Prospect"}
        </Typography>
        <IconButton
          onClick={handleClose}
          size='small'
          disabled={isLoading || isSavingRemarks}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {/* Loading State */}
        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={50} />
            <Typography variant='body1' color='textSecondary'>
              Preparing call service...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Prospect Information */}
            <Avatar className={classes.avatar}>
              {prospect.firstName?.charAt(0) || <PersonIcon fontSize='large' />}
            </Avatar>

            <Typography className={classes.prospectName}>
              {prospect.firstName} {prospect.lastName}
            </Typography>

            <Box className={classes.phoneNumber}>
              <PhoneIcon fontSize='small' />
              <Typography>{prospect.contactNo || "No phone number"}</Typography>
            </Box>

            {/* Call Status */}
            <Box className={classes.statusContainer}>
              <Typography
                className={`${classes.statusText} ${callStatus}`}
                variant='body1'>
                {getStatusMessage()}
              </Typography>

              {/* Call Duration */}
              {(isCallActive || showRemarks) && callDuration > 0 && (
                <Typography className={classes.callDuration}>
                  {getFormattedDuration()}
                </Typography>
              )}
            </Box>

            {/* Error Display */}
            {error && !saveSuccess && (
              <Box className={classes.errorBox}>
                <ErrorIcon fontSize='small' />
                <Typography variant='body2'>{error}</Typography>
              </Box>
            )}

            {/* Success Message */}
            {saveSuccess && (
              <Box className={classes.successMessage}>
                <Typography
                  variant='body2'
                  className={classes.successMessageTxt}>
                  ✓ Changes have been saved!
                </Typography>
              </Box>
            )}

            {/* Call Button (Only show if not showing remarks) */}
            {!showRemarks && (
              <IconButton
                className={`${classes.callButton} ${
                  isCallActive ? "endCall" : "makeCall"
                }`}
                onClick={isCallActive ? handleEndCall : handleMakeCall}
                disabled={
                  isLoading ||
                  !isInitialized ||
                  !prospect.contactNo ||
                  callStatus === "calling" ||
                  isSavingRemarks
                }
                size='large'>
                {callStatus === "calling" ? (
                  <CircularProgress size={30} color='inherit' />
                ) : isCallActive ? (
                  <CallEndIcon style={{ color: "white", fontSize: "32px" }} />
                ) : (
                  <CallIcon style={{ color: "white", fontSize: "32px" }} />
                )}
              </IconButton>
            )}

            {/* Additional Call Info */}
            {isCallActive && (
              <Box className={classes.callInfo}>
                <Typography variant='caption' color='textSecondary'>
                  Call in progress...
                </Typography>
              </Box>
            )}

            {/* Remarks Section (Shows after call ends) */}
            {showRemarks && !isCallActive && (
              <Box className={classes.remarksSection}>
                <Box className={classes.remarksHeader}>
                  <NoteIcon />
                  <Typography variant='h6'>Call Remarks</Typography>
                </Box>

                {/* Simple "Is Connect" Checkbox */}
                <Box className={classes.connectCheckbox}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isConnected}
                        onChange={handleConnectCheckboxChange}
                        color='primary'
                        disabled={isSavingRemarks || saveSuccess}
                      />
                    }
                    label='Is Contacted'
                  />
                </Box>

                <Typography variant='body2' color='textSecondary' gutterBottom>
                  Add notes about your conversation with {prospect.firstName}
                </Typography>

                <TextField
                  className={classes.remarksTextarea}
                  multiline
                  rows={4}
                  variant='outlined'
                  placeholder='Enter call summary, follow-up actions, or any important notes...'
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  disabled={isSavingRemarks || saveSuccess}
                  error={!!error && error.includes("Please either check")}
                  helperText={
                    error && error.includes("Please either check") ? error : ""
                  }
                />

                <Button
                  className={classes.saveButton}
                  variant='contained'
                  color='primary'
                  startIcon={saveSuccess ? null : <SaveIcon />}
                  onClick={handleSaveRemarks}
                  disabled={
                    !isSaveButtonEnabled() || isSavingRemarks || saveSuccess
                  }
                  fullWidth>
                  {isSavingRemarks ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    "✓ Saved!"
                  ) : (
                    "Save"
                  )}
                </Button>

                {!saveSuccess && (
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    style={{ display: "block", marginTop: 8 }}>
                    Call duration: {getFormattedDuration()}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          color='default'
          disabled={isLoading || callStatus === "calling" || isSavingRemarks}>
          {isCallActive ? "End Call & Close" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallWidget;
